import React, { useCallback, useEffect, useState } from 'react';
import { Card } from '@material-ui/core';
import { ListAlt as ListAltIcon } from '@material-ui/icons';

import { ApiParams, FetchError } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TestStepEditor from '../components/TestStepEditor/TestStepEditor';
import { TestStep } from '../components/TestStepEditor/TestStep.interface';
import {
  useFindAllTestSteps,
  useUpdateManyTestSteps
} from '../components/TestStepEditor/TestStep.hooks';

export interface TestStepCardProps {
  testSteps?: TestStep[];
  verificationId?: number;
  validationId?: number;
  hidden?: boolean;
  doFetch?: boolean;
  disabled?: boolean;
  onChange?: (testSteps: TestStep[]) => void;
  onUpdate?: () => void;
  onError?: (error: FetchError) => void;
}

const TestStepCard: React.FC<TestStepCardProps> = (
  props: TestStepCardProps
) => {
  const {
    testSteps: initialTestSteps,
    verificationId,
    validationId,
    hidden = false,
    doFetch = false,
    disabled = false,
    onChange,
    onError,
    onUpdate
  } = props;
  const [testSteps, setTestSteps] = useState<TestStep[] | undefined>(
    initialTestSteps
  );
  const [
    { data: testStepData, isLoading: isFinding, error: findError },
    findTestSteps
  ] = useFindAllTestSteps();
  const [
    { data: updatedTestSteps, isLoading: isUpdating, error: updateError },
    updateTestSteps
  ] = useUpdateManyTestSteps();
  const isLoading: boolean = isFinding || isUpdating;
  const error: FetchError | undefined = findError || updateError;

  const onChangeTestSteps = (
    testSteps: TestStep[],
    noSave: boolean = false
  ) => {
    // update
    !noSave && updateTestSteps(testSteps);
    setTestSteps(testSteps);
    onChange?.(testSteps);
  };

  useEffect(() => {
    initialTestSteps && setTestSteps(initialTestSteps);
  }, [initialTestSteps]);

  const findTestStepsWithParams = useCallback(() => {
    const params: ApiParams = {};
    if (verificationId) {
      params.verificationId = verificationId;
    }
    if (validationId) {
      params.validationId = validationId;
    }
    findTestSteps(params);
  }, [findTestSteps, verificationId, validationId]);

  useEffect(() => {
    if (doFetch || verificationId || validationId) {
      findTestStepsWithParams();
    }
  }, [findTestStepsWithParams, doFetch, verificationId, validationId]);

  useEffect(() => {
    if (testStepData) {
      setTestSteps(testStepData);
    }
  }, [testStepData]);

  useEffect(() => {
    if (updatedTestSteps) {
      // findTestStepsWithParams()
      setTestSteps(updatedTestSteps);
      onUpdate?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [updatedTestSteps]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <Card style={hidden ? { display: 'none' } : undefined}>
      <CardTitle>
        <ListAltIcon />
        Test Script Editor
      </CardTitle>
      {testSteps && (
        <TestStepEditor
          testSteps={testSteps}
          isLoading={isLoading}
          validationId={validationId}
          verificationId={verificationId}
          disabled={disabled}
          onChange={onChangeTestSteps}
        />
      )}
    </Card>
  );
};

export default TestStepCard;
